/*
 * /patient/me — patient self-service portal
 */
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	cn,
	Container,
	DatePickerFormField,
	Divider,
	FormButtons,
	InputFormField,
	Page,
	PageHeader,
	Placeholder,
	SelectFormField,
	toast,
	TRPCForm,
} from "@emr/react-ui"
import type { Patient } from "@emr/db/client"
import { updatePatientSchema } from "@emr/db/client"
import { createFileRoute } from "@tanstack/react-router"
import { CheckCircle, FileText, Pen, User } from "lucide-react"
import { useState } from "react"
import { useAuth } from "@emr/auth/client"
import { z } from "zod"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"
import { SignaturePad } from "../../features/patient-portal/signature-pad"
import { ConsentFormViewer } from "../../features/patient-portal/consent-form-viewer"

export const Route = createFileRoute("/patient/me")({
	component: PatientPortalPage,
	loader: rolePageLoader(["patient"]),
})

/* ─── Tab definition ─────────────────────────────────────────────────────── */

type Tab = "profile" | "signature" | "documents"
const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
	{ id: "profile", label: "My Profile", icon: User },
	{ id: "signature", label: "Signature", icon: Pen },
	{ id: "documents", label: "Documents", icon: FileText },
]

/* ─── Page ───────────────────────────────────────────────────────────────── */

function PatientPortalPage() {
	const { user } = useAuth()
	const [activeTab, setActiveTab] = useState<Tab>("profile")
	const utils = trpc.useUtils()

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery(
			{ where: { userId: user?.id } },
			{ enabled: !!user?.id },
		)

	const { data: signature, isLoading: sigLoading } =
		trpc.patientSignature.getByFilter.useQuery(
			{ where: { patientId: patient?.id, active: true } },
			{ enabled: !!patient?.id },
		)

	const { data: consentForms } = trpc.fileAttachment.getAllByFilter.useQuery(
		{
			where: { patientId: patient?.id, entityType: "consent_form", active: true },
		},
		{ enabled: !!patient?.id },
	)

	const upsertSignature = trpc.patientSignature.upsert.useMutation({
		onSuccess: () => {
			utils.patientSignature.getByFilter.invalidate()
			toast.success("Signature saved")
		},
		onError: () => toast.error("Failed to save signature"),
	})

	const uploadConsent = trpc.fileAttachment.uploadBase64.useMutation({
		onSuccess: () => {
			utils.fileAttachment.getAllByFilter.invalidate()
			toast.success("Consent form submitted and saved")
		},
		onError: () => toast.error("Failed to submit consent form — please try again"),
	})

	const isLoading = patientLoading || sigLoading

	if (isLoading) {
		return (
			<Page>
				<Container>
					<Placeholder title="Loading your profile…" />
				</Container>
			</Page>
		)
	}

	if (!patient) {
		return (
			<Page>
				<Container>
					<Placeholder title="No patient record found for your account." />
				</Container>
			</Page>
		)
	}

	const patientName = `${patient.firstName} ${patient.lastName}`

	return (
		<Page centerVertical={false}>
			<Container width="lg" padding="lg" gap="lg">
				<PageHeader title="My Portal" />

				{/* ─── Tab bar ────────────────────────────────────────────── */}
				<div className="flex gap-0 border-b">
					{TABS.map(({ id, label, icon: Icon }) => (
						<button
							key={id}
							onClick={() => setActiveTab(id)}
							className={cn(
								"flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors whitespace-nowrap relative",
								"hover:text-foreground",
								activeTab === id
									? "text-primary after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:rounded-t-full"
									: "text-muted-foreground",
							)}
						>
							<Icon className="w-4 h-4" />
							{label}
						</button>
					))}
				</div>

				{/* ─── Profile tab ────────────────────────────────────────── */}
				{activeTab === "profile" && (
					<Card>
						<CardHeader>
							<CardTitle>Personal Information</CardTitle>
						</CardHeader>
						<CardContent>
							<PatientPersonalForm patient={patient} />
						</CardContent>
					</Card>
				)}

				{/* ─── Signature tab ───────────────────────────────────────── */}
				{activeTab === "signature" && (
					<Card>
						<CardHeader>
							<CardTitle>My Signature</CardTitle>
						</CardHeader>
						<CardContent className="space-y-6">
							{signature?.signatureData && (
								<div className="space-y-2">
									<p className="text-sm font-medium text-muted-foreground">
										Current signature on file
									</p>
									<div className="rounded-md border bg-white p-3 inline-block">
										<img
											src={signature.signatureData}
											alt="Current signature"
											className="h-16 max-w-xs object-contain"
										/>
									</div>
									<p className="text-xs text-muted-foreground">
										Draw a new signature below to replace it.
									</p>
								</div>
							)}

							<div>
								<p className="text-sm font-medium mb-3">
									{signature ? "Update signature" : "Add your signature"}
								</p>
								<SignaturePad
									onSave={(dataUrl) =>
										upsertSignature.mutate({
											patientId: patient.id,
											signatureData: dataUrl,
										})
									}
								/>
							</div>
						</CardContent>
					</Card>
				)}

				{/* ─── Documents tab ───────────────────────────────────────── */}
				{activeTab === "documents" && (
					<div className="space-y-6">
						{/* Previously signed forms */}
						{consentForms && consentForms.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle>Signed Documents</CardTitle>
								</CardHeader>
								<CardContent className="space-y-3">
									{consentForms.map((f) => (
										<div
											key={f.id}
											className="flex items-center gap-3 rounded-md border p-3"
										>
											<CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
											<div className="flex-1 min-w-0">
												<p className="text-sm font-medium truncate">{f.fileName}</p>
												<p className="text-xs text-muted-foreground">
													Signed on{" "}
													{new Date(f.createdAt).toLocaleDateString("en-CA", {
														year: "numeric",
														month: "short",
														day: "numeric",
													})}
												</p>
											</div>
										</div>
									))}
								</CardContent>
							</Card>
						)}

						{/* Consent form to sign */}
						<Card>
							<CardHeader>
								<CardTitle>Patient Consent & Release Agreement</CardTitle>
							</CardHeader>
							<CardContent>
								{!signature && (
									<div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
										You need a signature on file before you can sign documents.{" "}
										<button
											className="underline font-medium"
											onClick={() => setActiveTab("signature")}
										>
											Add your signature →
										</button>
									</div>
								)}
								<ConsentFormViewer
									patientName={patientName}
									signatureDataUrl={signature?.signatureData ?? null}
									isSubmitting={uploadConsent.isPending}
									onSubmit={(base64Pdf) => {
										const today = new Date().toISOString().slice(0, 10)
										uploadConsent.mutate({
											base64Data: base64Pdf,
											mimeType: "application/pdf",
											fileName: `consent-form-${today}.pdf`,
											entityType: "consent_form",
											patientId: patient.id,
										})
									}}
								/>
							</CardContent>
						</Card>
					</div>
				)}
			</Container>
		</Page>
	)
}

/* ─── Personal info form (no medical fields) ─────────────────────────────── */

const PREFERRED_CONTACT_OPTIONS = [
	{ label: "Text", value: "text" },
	{ label: "Phone", value: "phone" },
	{ label: "Email", value: "email" },
]

const MARITAL_STATUS_OPTIONS = [
	{ label: "Single", value: "single" },
	{ label: "Married / Common Law", value: "married" },
	{ label: "Separated", value: "separated" },
	{ label: "Divorced", value: "divorced" },
	{ label: "Widowed", value: "widowed" },
]

const portalUpdateSchema = updatePatientSchema.extend({
	id: z.string().uuid(),
})

function PatientPersonalForm({ patient }: { patient: Patient }) {
	const utils = trpc.useUtils()

	return (
		<TRPCForm<Patient>
			entity={patient}
			createSchema={portalUpdateSchema as any}
			createTrpc={trpc.patient.update as any}
			updateSchema={portalUpdateSchema as any}
			updateTrpc={trpc.patient.update}
			invalidate={() => {
				utils.patient.getByFilter.invalidate()
			}}
			onSuccess={() => toast.success("Profile updated")}
			className="flex flex-col gap-6"
		>
			{/* Name */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<InputFormField name="firstName" label="First Name" placeholder="Jane" required />
				<InputFormField name="lastName" label="Last Name" placeholder="Smith" required />
			</div>

			<DatePickerFormField name="dateOfBirth" label="Date of Birth" />

			<Divider />

			{/* Contact */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<InputFormField name="phone" label="Phone" placeholder="+1 (555) 000-0000" />
				<InputFormField name="phone2" label="Secondary Phone" placeholder="+1 (555) 000-0000" />
			</div>
			<InputFormField name="email" label="Email" placeholder="jane@example.com" />
			<SelectFormField
				name="preferredContact"
				label="Preferred Method of Contact"
				options={PREFERRED_CONTACT_OPTIONS}
				placeholder="Select method"
			/>

			<Divider />

			{/* Personal */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<SelectFormField
					name="maritalStatus"
					label="Marital Status"
					options={MARITAL_STATUS_OPTIONS}
					placeholder="Select status"
				/>
				<InputFormField name="occupation" label="Occupation" placeholder="e.g. Retired" />
			</div>
			<InputFormField name="employer" label="Employer" placeholder="None" />

			<Divider />

			{/* Emergency contact */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<InputFormField
					name="emergencyContactName"
					label="Emergency Contact Name"
					placeholder="John Smith"
				/>
				<InputFormField
					name="emergencyContactRelationship"
					label="Relationship"
					placeholder="e.g. Spouse"
				/>
				<InputFormField
					name="emergencyContactPhone"
					label="Emergency Contact Phone"
					placeholder="+1 (555) 000-0000"
				/>
			</div>

			<FormButtons submitLabel="Save Profile" />
		</TRPCForm>
	)
}
